import React, { useState, useEffect, useRef } from 'react';
import { Checkbox } from '@h2/ui';
import { DollarSign, Star, Zap } from 'lucide-react';

interface PrioritySelectorProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  className?: string;
  isPremium?: boolean;
}

export const PrioritySelector: React.FC<PrioritySelectorProps> = ({
  value = [],
  onChange,
  className = '',
  isPremium = false
}) => {
  const [selected, setSelected] = useState<string[]>(value);
  const premiumInitialized = useRef(false);

  // value prop이 변경되면 내부 상태 업데이트
  useEffect(() => {
    setSelected(value);
  }, [value]);

  // 프리미엄이 활성화되면 품질을 기본 선택 (한 번만)
  useEffect(() => {
    if (isPremium && !premiumInitialized.current && selected.length === 0 && !selected.includes('quality')) {
      premiumInitialized.current = true;
      const newSelected = ['quality'];
      setSelected(newSelected);
      onChange?.(newSelected);
    }
    
    // 프리미엄이 비활성화되면 초기화 플래그 리셋
    if (!isPremium) {
      premiumInitialized.current = false;
    }
  }, [isPremium, selected.length, onChange]);

  const options = [
    { id: 'cost', label: '비용', icon: DollarSign, color: 'text-blue-600' },
    { id: 'quality', label: '품질', icon: Star, color: 'text-yellow-600' },
    { id: 'time', label: '시간', icon: Zap, color: 'text-red-600' }
  ];

  const handleToggle = (optionId: string) => {
    let newSelected = [...selected];
    
    if (newSelected.includes(optionId)) {
      // 이미 선택된 경우 제거
      newSelected = newSelected.filter(id => id !== optionId);
    } else {
      // 새로 선택하는 경우
      if (newSelected.length >= 2) {
        // 최대 2개까지만 선택 가능
        return;
      }
      newSelected.push(optionId);
    }

    setSelected(newSelected);
    onChange?.(newSelected);
  };

  const getDescription = () => {
    const sortedSelected = selected.sort();
    const key = sortedSelected.join(',');
    
    const descriptions: Record<string, string> = {
      '': '우선순위를 선택해주세요',
      'cost': '하급 작업자 매칭',
      'quality': '상급 작업자 매칭',
      'time': '분할작업',
      'quality,time': '상급 작업자 매칭, 긴급 작업 추가금',
      'cost,time': '분할, 하급작업자 매칭'
    };

    return descriptions[key] || '';
  };

  const isDisabled = (optionId: string) => {
    // 이미 선택된 경우는 비활성화하지 않음 (해제 가능)
    if (selected.includes(optionId)) {
      return false;
    }
    
    // 최대 2개 선택되었거나, 품질+비용 조합인 경우
    if (selected.length >= 2) {
      return true;
    }
    
    // 품질+비용 조합 방지
    if ((selected.includes('quality') && optionId === 'cost') || 
        (selected.includes('cost') && optionId === 'quality')) {
      return true;
    }
    
    return false;
  };


  return (
    <div className={`space-y-4 ${className}`}>
      {/* 헤더 */}
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-gray-900">작업 우선순위</h3>
        <p className="text-xs text-gray-500">작업의 우선순위를 선택하세요 자동매칭에 반영됩니다</p>
      </div>

      {/* 체크박스 옵션들 - 3x1 그리드 */}
      <div className="grid grid-cols-3 gap-3">
        {options.map((option) => {
          const isChecked = selected.includes(option.id);
          const disabled = isDisabled(option.id);
          const IconComponent = option.icon;
          
          return (
            <div 
              key={option.id}
              className={`flex flex-col items-center p-4 rounded-lg border transition-all ${
                isChecked 
                  ? 'border-blue-300 bg-blue-50 shadow-sm' 
                  : disabled 
                    ? 'border-gray-200 bg-gray-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              } ${disabled && !isChecked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => !disabled && handleToggle(option.id)}
            >
              <div className="flex flex-col items-center space-y-2">
                <IconComponent 
                  size={24} 
                  className={`${
                    disabled && !isChecked ? 'text-gray-400' : option.color
                  }`}
                />
                <span className={`text-sm font-medium ${
                  disabled && !isChecked ? 'text-gray-400' : 'text-gray-700'
                }`}>
                  {option.label}
                </span>
              </div>
              <Checkbox
                id={option.id}
                checked={isChecked}
                disabled={disabled}
                onChange={() => handleToggle(option.id)}
                className="mt-2 data-[state=checked]:bg-blue-600"
              />
            </div>
          );
        })}
      </div>

      {/* 경고 메시지만 표시 */}
      {selected.includes('quality') && selected.includes('cost') && (
        <div className="text-xs text-red-500">
          품질+비용 조합은 선택할 수 없습니다
        </div>
      )}

      {/* 안내 문구 */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="text-sm font-medium text-gray-700 mb-1">
          선택 결과
        </div>
        <div className="text-sm text-gray-600">
          {getDescription()}
        </div>
      </div>
    </div>
  );
};
